import type { AdminOverview, AdminUser } from "../../domain/app-types.ts";
import { administrationDialogView, adminQuotaDialogView, type AdminAuditEvent } from "./view.ts";
import { createDialogOperations } from "../../ui/dialog-operations.ts";
import { bindActions } from "../../app/actions.ts";
import { formatStorageBytes } from "./view.ts";

interface ConfirmOptions { title: string; message: string; confirmLabel: string }
interface TextOptions extends ConfirmOptions { label: string }
interface Quota { attachment_bytes: number; entity_count: number; change_count: number }

export interface AdministrationCapabilities {
  request(path: string, init?: RequestInit): Promise<Response>;
  apiError(response: Response, fallback: string): Promise<string>;
  confirm(options: ConfirmOptions): Promise<boolean>;
  text(options: TextOptions): Promise<string | null>;
  icon(name: string): string;
  formatTimestamp(value: string): string;
  toast(message: string, error?: boolean): void;
}

export function validQuota(quota: Quota): boolean {
  return Object.values(quota).every(value => Number.isSafeInteger(value) && value >= 0);
}

function quotaDialog(account: AdminUser, overview: AdminOverview, capabilities: AdministrationCapabilities): Promise<Quota | null> {
  return new Promise(resolve => {
    const dialog = document.createElement("dialog"); dialog.className = "name-dialog";
    const operations = createDialogOperations(dialog, capabilities.toast);
    operations.render(adminQuotaDialogView(account, overview));
    dialog.querySelector<HTMLInputElement>('[name="attachment_bytes"]')!.addEventListener("input", event => {
      const value = (event.currentTarget as HTMLInputElement).valueAsNumber;
      dialog.querySelector("[data-quota-storage-preview]")!.textContent = Number.isSafeInteger(value) && value >= 0 ? formatStorageBytes(value) + " · Exact bytes retained" : "Enter non-negative whole bytes";
    });
    let submitted = false;
    dialog.querySelector("[data-cancel]")!.addEventListener("click", () => dialog.close());
    operations.listen("form", "submit", async event => {
      event.preventDefault(); const data = new FormData(event.currentTarget as HTMLFormElement);
      const quota = { attachment_bytes: Number(data.get("attachment_bytes")), entity_count: Number(data.get("entity_count")), change_count: Number(data.get("change_count")) };
      if (!validQuota(quota)) throw new Error("Quotas must be non-negative whole numbers");
      const response = await capabilities.request(`/api/v1/admin/users/${account.id}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(quota) });
      if (!response.ok) throw new Error(await capabilities.apiError(response, "Could not update quota"));
      submitted = true; resolve(quota); dialog.close();
    });
    dialog.addEventListener("close", () => { dialog.remove(); if (!submitted) resolve(null); });
    document.body.append(dialog); dialog.showModal();
  });
}

export async function openAdministrationDialog(capabilities: AdministrationCapabilities): Promise<void> {
  const opener = document.activeElement as HTMLElement | null;
  const dialog = document.createElement("dialog"); dialog.className = "name-dialog admin-dialog";
  const operations = createDialogOperations(dialog, capabilities.toast);
  let overview: AdminOverview, users: AdminUser[], events: AdminAuditEvent[];
  let section = "system", query = "";
  const focusAccount = (id: string) => requestAnimationFrame(() => {
    const row = Array.from(dialog.querySelectorAll<HTMLElement>("[data-admin-account]")).find(item => item.dataset.adminAccount === id);
    row?.querySelector<HTMLElement>("summary")?.focus({ preventScroll: true });
  });
  const render = () => {
    const scroll = dialog.scrollTop;
    operations.render(administrationDialogView(overview, users, events, capabilities.icon, capabilities.formatTimestamp));
    const select = () => {
      dialog.querySelectorAll<HTMLElement>("[data-admin-section]").forEach(panel => { panel.hidden = panel.dataset.adminSection !== section; });
      dialog.querySelectorAll<HTMLElement>("[data-admin-section-open]").forEach(button => button.setAttribute("aria-pressed", String(button.dataset.adminSectionOpen === section)));
    };
    select();
    dialog.querySelectorAll<HTMLButtonElement>("[data-admin-section-open]").forEach(button => button.addEventListener("click", () => { section = button.dataset.adminSectionOpen!; select(); }));
    const search = dialog.querySelector<HTMLInputElement>("[data-admin-search]")!; search.value = query;
    const filter = () => {
      let count = 0;
      dialog.querySelectorAll<HTMLElement>("[data-admin-account]").forEach(row => { row.hidden = !users.find(account => account.id === row.dataset.adminAccount)?.email.toLowerCase().includes(query.toLowerCase().trim()); if (!row.hidden) count++; });
      dialog.querySelector("[data-admin-account-count]")!.textContent = `${count} of ${users.length} loaded accounts`;
      (dialog.querySelector("[data-admin-account-empty]") as HTMLElement).hidden = count !== 0;
    };
    search.addEventListener("input", () => { query = search.value; filter(); }); filter();
    dialog.querySelector("[data-admin-close]")!.addEventListener("click", () => dialog.close());
    bindActions(dialog, () => {});
    const closeMenu = (button: HTMLElement) => { const menu = button.closest("details"); menu?.removeAttribute("open"); menu?.querySelector<HTMLElement>("summary")?.focus({ preventScroll: true }); };
    operations.listen("[data-admin-compact]", "click", async () => {
      if (!await capabilities.confirm({ title: "Compact synchronization history?", message: "Current snapshots remain authoritative. Clients behind this point will receive a complete snapshot reset.", confirmLabel: "Compact" })) return;
      const response = await capabilities.request("/api/v1/admin/compact", { method: "POST" });
      if (!response.ok) throw new Error(await capabilities.apiError(response, "Could not compact history"));
      const result = await response.json(); overview.changes = 0; overview.compaction.removed_changes += result.removed_changes;
      render(); operations.toast("Synchronization history compacted");
    });
    operations.listen("[data-admin-disable]", "click", async event => {
      const button = event.currentTarget as HTMLButtonElement; closeMenu(button);
      const account = users.find(item => item.id === button.dataset.adminDisable); if (!account) return;
      const disabling = !account.disabled_at;
      let reason = "";
      if (disabling) { reason = (await capabilities.text({ title: `Disable ${account.email}?`, message: "Active sessions will be revoked while data remains intact.", label: "Reason", confirmLabel: "Disable account" }))?.trim() || ""; if (!reason) return; }
      else if (!await capabilities.confirm({ title: `Enable ${account.email}?`, message: "This account will be able to sign in again.", confirmLabel: "Enable account" })) return;
      const response = await capabilities.request(`/api/v1/admin/users/${account.id}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify({ disabled: disabling, reason }) });
      if (!response.ok) throw new Error(await capabilities.apiError(response, "Could not update account"));
      account.disabled_at = disabling ? new Date().toISOString() : null; account.disabled_reason = disabling ? reason : null;
      render(); operations.toast(disabling ? "Account disabled" : "Account enabled"); focusAccount(account.id);
    });
    operations.listen("[data-admin-quota]", "click", async event => {
      const button = event.currentTarget as HTMLButtonElement; closeMenu(button);
      const account = users.find(item => item.id === button.dataset.adminQuota); if (!account) return;
      const quota = await quotaDialog(account, overview, capabilities); if (!quota) return;
      account.quota = { ...quota }; render(); operations.toast("Quotas updated"); focusAccount(account.id);
    });
    dialog.scrollTop = scroll;
  };
  const load = async () => {
    const responses = await Promise.all(["overview", "users", "audit?limit=100"].map(path => capabilities.request("/api/v1/admin/" + path)));
    for (const response of responses) if (!response.ok) throw new Error(await capabilities.apiError(response, "Could not load administration data"));
    [overview, users, events] = await Promise.all(responses.map(response => response.json()));
    render();
  };
  operations.render('<div class="share-dialog__header"><h2>Instance operations</h2><button type="button" class="button button--soft" data-admin-close>Close administration</button></div><div class="ui-dialog__body"><p>Restricted administration data</p><button type="button" class="button button--soft" data-admin-retry>Retry loading</button></div>');
  dialog.querySelector("[data-admin-close]")!.addEventListener("click", () => dialog.close());
  operations.listen("[data-admin-retry]", "click", load);
  document.body.append(dialog); dialog.addEventListener("close", () => { dialog.remove(); if (opener?.isConnected) opener.focus({ preventScroll: true }); }); dialog.showModal();
  await operations.run(load);
}
